import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Tag, Tree, Typography, Modal, Descriptions, Divider, Grid, Table } from "antd";

import css from "./OrdersInfoSingleDishModal.module.css";
import { dishesActions } from "../../../../store/slices/dishes-slices";
import { ImageReusable } from "../../../../ui";

const OrdersInfoSingleDishModal = ({ setIsShowSingleDish, selectedDishId }) => {
  const dispatch = useDispatch();
  const { singleDish } = useSelector((store) => store.dishes);
  const { t, i18n } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const productsBlockRef = useRef(null);

  const {
    name,
    img,
    products,
    semiFinisheds,
    carbs,
    energy,
    fats,
    proteins,
    weight,
    weightNetto,
    recipe,
    ingredients,
  } = singleDish;

  const currentLanguage = i18n.language;
  const displayName = name?.[currentLanguage] || name?.["en"] || "Unknown Dish";
  const displayRecipe =
    recipe?.[currentLanguage] || recipe?.["en"] || "Unknown Recipe";

  const buildTreeData = () => {
    const nodes = [];

    (products ?? []).forEach((p) => {
      const pName = p.name?.[currentLanguage] || p.name?.["en"] || "Unknown";
      nodes.push({
        key: p.id,
        isLeaf: true,
        title: (
          <span>
            <Typography.Text>{pName}</Typography.Text>
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {p.weight} g
            </Tag>
          </span>
        ),
      });
    });

    (semiFinisheds ?? []).forEach((sf) => {
      const sfName = sf.name?.[currentLanguage] || sf.name?.["en"] || "Unknown";
      const children = (sf.semiFinished?.products ?? []).map((obj, i) => {
        const itemName =
          obj.product?.name?.[currentLanguage] ||
          obj.product?.name?.["en"] ||
          "Unknown";
        return {
          key: `${sf.id}-${i}`,
          isLeaf: true,
          title: (
            <span>
              <Typography.Text>{itemName}</Typography.Text>
              <Tag color="geekblue" style={{ marginLeft: 8 }}>
                {obj.weight} g
              </Tag>
            </span>
          ),
        };
      });

      nodes.push({
        key: sf.id,
        title: (
          <span>
            <Typography.Text strong>{sfName}</Typography.Text>
            <Tag color="purple" style={{ marginLeft: 8 }}>
              {sf.weight} g
            </Tag>
          </span>
        ),
        children,
      });
    });

    return nodes;
  };

  useEffect(() => {
    dispatch(dishesActions.getDishById({ id: selectedDishId }));
  }, [dispatch, selectedDishId]);

  useEffect(() => {
    const checkScroll = () => {
      if (productsBlockRef.current) {
        const { scrollHeight, clientHeight, scrollTop } =
          productsBlockRef.current;
        const hasScrollableContent = scrollHeight > clientHeight;
        const isScrolledToBottom =
          scrollTop + clientHeight >= scrollHeight - 10;

        setShowScrollIndicator(hasScrollableContent && !isScrolledToBottom);
      }
    };

    const productsBlock = productsBlockRef.current;
    if (productsBlock) {
      productsBlock.addEventListener("scroll", checkScroll);
      checkScroll();
    }

    return () => {
      if (productsBlock) {
        productsBlock.removeEventListener("scroll", checkScroll);
      }
    };
  }, [products, semiFinisheds]);

  const nutritionItems = [
    {
      key: "proteins",
      label: t("modal-proteins"),
      children: `${proteins} g`,
    },
    { key: "fats", label: t("modal-fats"), children: `${fats} g` },
    {
      key: "carbs",
      label: t("modal-carbohydrates"),
      children: `${carbs} g`,
    },
    { key: "energy", label: t("modal-energy"), children: `${energy} g` },
    { key: "weight", label: t("Weight"), children: `${weight || 0} g` },
    {
      key: "weightNetto",
      label: t("weight-netto"),
      children: `${weightNetto || 0} g`,
    },
  ];

  return (
    <Modal
      open={true}
      onCancel={() => setIsShowSingleDish(false)}
      footer={null}
      width={screens.md ? "70%" : "95%"}
      // style={{ top: screens.md 30 }}
      styles={{
        body: {
          padding: screens.md ? "16px 24px" : "12px 16px",
          overflowY: "auto",
          maxHeight: screens.md ? "80vh" : "65vh",
        },
      }}
      title={
        <Typography.Title level={screens.md ? 4 : 5} style={{ margin: 0 }}>
          {displayName}
        </Typography.Title>
      }
    >
      <div className={css.blockInfo}>
        <div className={css.leftBlock}>
          <div className={css.imgBlock}>
            <ImageReusable src={img} alt="Food Image" />
          </div>

          <div className={css.recipeSection}>
            <Divider orientation="left" orientationMargin={0}>
              <Typography.Text strong>{t("Recipe")}</Typography.Text>
            </Divider>
            <Typography.Paragraph className={css.recipeSectionContent}>
              {displayRecipe}
            </Typography.Paragraph>
          </div>
        </div>

        <div className={css.rightBlock}>
          <Descriptions
            bordered
            size="small"
            column={screens.md ? 3 : screens.sm ? 2 : 1}
            items={nutritionItems}
          />

          {/* ingredients block */}
          <div className={css.ingredientsBlock}>
            <Divider orientation="left" orientationMargin={0}>
              <Typography.Text strong>{t("Ingredients")}</Typography.Text>
            </Divider>
            <Table
              size="small"
              dataSource={(ingredients ?? []).map((ingredient, idx) => ({
                key: ingredient.id ?? idx,
                name:
                  ingredient.name?.[currentLanguage] ||
                  ingredient.name?.["en"] ||
                  "—",
                weight: ingredient.weight != null ? `${ingredient.weight} g` : "—",
                weightNetto:
                  ingredient.weightNetto != null
                    ? `${ingredient.weightNetto} g`
                    : "—",
                isSemiFinished: ingredient.isSemiFinished,
                subIngredients: ingredient.ingredients ?? [],
              }))}
              columns={[
                {
                  title: t("name"),
                  dataIndex: "name",
                  key: "name",
                  ellipsis: true,
                  render: (text, record) =>
                    record.isSemiFinished ? (
                      <Typography.Text strong>{text}</Typography.Text>
                    ) : (
                      text
                    ),
                },
                {
                  title: t("Weight"),
                  dataIndex: "weight",
                  key: "weight",
                  width: 90,
                  align: "center",
                },
                {
                  title: t("weight-netto"),
                  dataIndex: "weightNetto",
                  key: "weightNetto",
                  width: 110,
                  align: "center",
                },
              ]}
              expandable={{
                rowExpandable: (record) =>
                  record.isSemiFinished && record.subIngredients.length > 0,
                expandedRowRender: (record) => (
                  <Table
                    size="small"
                    dataSource={record.subIngredients.map((sub, i) => ({
                      key: sub.id ?? i,
                      name:
                        sub.name?.[currentLanguage] ||
                        sub.name?.["en"] ||
                        "—",
                      weight: sub.weight != null ? `${sub.weight} g` : "—",
                      weightNetto:
                        sub.weightNetto != null
                          ? `${sub.weightNetto} g`
                          : "—",
                    }))}
                    columns={[
                      {
                        title: t("name"),
                        dataIndex: "name",
                        key: "name",
                        ellipsis: true,
                      },
                      {
                        title: t("Weight"),
                        dataIndex: "weight",
                        key: "weight",
                        width: 90,
                        align: "center",
                      },
                      {
                        title: t("weight-netto"),
                        dataIndex: "weightNetto",
                        key: "weightNetto",
                        width: 110,
                        align: "center",
                      },
                    ]}
                    pagination={false}
                    showHeader={false}
                    className={css.subIngredientsTable}
                  />
                ),
              }}
              pagination={false}
              locale={{ emptyText: t("No ingredients available") }}
              style={{ width: "100%" }}
            />
          </div>

          <div className={css.productsBlock} ref={productsBlockRef}>
            <Divider orientation="left" orientationMargin={0}>
              <Typography.Text strong>{t("products-and-semi-finished")}</Typography.Text>
            </Divider>
            <Tree
              key={singleDish?.id}
              treeData={buildTreeData()}
              defaultExpandAll
              selectable={false}
              style={{ width: "100%" }}
            />

            {showScrollIndicator && (
              <div className={css.scrollIndicator}></div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { OrdersInfoSingleDishModal };
